import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { MapPin, DollarSign, Briefcase, Shield, ArrowUpRight, Bookmark, BookmarkCheck, ArrowLeft, Zap, Star, BadgeCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirect = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [saved, setSaved] = useState(false);
  const [applied, setApplied] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data));
    api.post(`/jobs/${id}/track`, { kind: "view" }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!user) return;
    api.get("/saved-jobs").then((r) => setSaved(r.data.some((j) => j.id === id))).catch(() => {});
    api.get("/applications").then((r) => setApplied(r.data.some((a) => a.job_id === id))).catch(() => {});
    api.get("/profile/me").then((r) => setHasProfile(Boolean(r.data?.headline || (r.data?.skills || []).length))).catch(() => {});
  }, [user, id]);

  const toggleSave = async () => {
    if (!user) { toast.info("Sign in to save jobs"); loginWithGoogle(); return; }
    try {
      if (saved) { await api.delete(`/saved-jobs/${id}`); toast.success("Removed"); setSaved(false); }
      else { await api.post(`/saved-jobs/${id}`); toast.success("Saved"); setSaved(true); }
    } catch { toast.error("Something went wrong"); }
  };

  const handleApply = async () => {
    if (user) {
      try {
        if (!applied) {
          await api.post("/applications", { job_id: id, status: "applied" });
          setApplied(true);
          toast.success(hasProfile ? "One-click applied. Tracker updated." : "Tracked. Complete your profile for one-click apply.");
        }
      } catch {}
    } else {
      api.post(`/jobs/${id}/track`, { kind: "apply" }).catch(() => {});
    }
    window.open(job.apply_url, "_blank", "noopener,noreferrer");
  };

  if (!job) return <div className="max-w-7xl mx-auto p-12 text-zinc-500 font-mono-alt uppercase text-sm">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/jobs" data-testid="job-back" className="text-sm text-zinc-500 hover:text-yellow-500 uppercase tracking-wider flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> Back to jobs
      </Link>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest">{job.industry}</div>
            {job.featured && (
              <div className="text-[10px] font-bold uppercase tracking-widest bg-yellow-500 text-black px-2 py-0.5 flex items-center gap-1"><Star size={10} /> Featured</div>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-6xl uppercase leading-none">{job.title}</h1>
          <Link to={`/employers/${job.employer_id}`} className="mt-3 inline-flex items-center gap-1 text-zinc-300 hover:text-yellow-500 text-lg">
            {job.employer}
            {job.verified_employer && <BadgeCheck size={16} className="text-yellow-500" />}
          </Link>

          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex items-center gap-1 text-sm text-zinc-400 border border-zinc-800 px-3 py-1"><MapPin size={14} className="text-yellow-500" />{job.location}</div>
            <div className="flex items-center gap-1 text-sm text-zinc-400 border border-zinc-800 px-3 py-1"><DollarSign size={14} className="text-yellow-500" />${job.wage_min}-${job.wage_max}/{job.wage_unit}</div>
            <div className="flex items-center gap-1 text-sm text-zinc-400 border border-zinc-800 px-3 py-1"><Briefcase size={14} className="text-yellow-500" />{job.type}</div>
            {job.registered_partner && (
              <div className="flex items-center gap-1 text-xs uppercase tracking-wider font-bold border border-yellow-500/40 text-yellow-500 px-3 py-1">
                <Shield size={12} /> Registered Partner
              </div>
            )}
          </div>

          <div className="mt-10">
            <h2 className="font-display text-2xl uppercase tracking-wider mb-3">About this Apprenticeship</h2>
            <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{job.description}</p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-display text-xl uppercase tracking-wider mb-3 text-yellow-500">Requirements</h3>
              <ul className="space-y-2 text-zinc-300 text-sm">
                {(job.requirements || []).map((r, i) => (<li key={i} className="flex gap-2"><span className="text-yellow-500">/</span>{r}</li>))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-xl uppercase tracking-wider mb-3 text-yellow-500">Benefits</h3>
              <ul className="space-y-2 text-zinc-300 text-sm">
                {(job.benefits || []).map((b, i) => (<li key={i} className="flex gap-2"><span className="text-yellow-500">/</span>{b}</li>))}
              </ul>
            </div>
          </div>

          <div className="mt-10">
            <h3 className="font-display text-xl uppercase tracking-wider mb-3 text-yellow-500">Skills You'll Learn</h3>
            <div className="flex flex-wrap gap-2">
              {(job.skills || []).map((s) => (<span key={s} className="text-xs font-mono-alt uppercase tracking-widest border border-zinc-800 px-3 py-1 text-zinc-300">{s}</span>))}
            </div>
          </div>
        </div>

        <aside className="md:col-span-1">
          <div className="border border-zinc-800 bg-zinc-950 p-6 sticky top-24">
            <div className="text-xs font-mono-alt uppercase text-zinc-500 tracking-widest mb-1">Apply Now</div>
            <div className="font-display text-2xl uppercase leading-none mb-4">Ready to <span className="text-yellow-500">start</span>?</div>

            <button
              onClick={handleApply}
              data-testid="job-apply-btn"
              className="block w-full bg-yellow-500 text-black text-center font-bold uppercase tracking-wider py-3 hover:bg-yellow-400 active:scale-95 transition"
            >
              {applied ? "Applied · Reopen" : (user && hasProfile ? "One-Click Apply" : "Apply Now")} <ArrowUpRight size={14} className="inline ml-1" />
            </button>

            {user && !hasProfile && (
              <Link to="/profile" className="mt-3 flex items-center justify-center gap-1 text-xs text-yellow-500 hover:text-yellow-400 uppercase tracking-wider">
                <Zap size={12} /> Enable one-click apply
              </Link>
            )}

            <button
              onClick={toggleSave}
              data-testid="job-save-btn"
              className="mt-3 w-full border border-zinc-800 hover:border-yellow-500 text-zinc-300 hover:text-yellow-500 uppercase tracking-wider text-sm py-3 flex items-center justify-center gap-2 transition"
            >
              {saved ? <><BookmarkCheck size={14} /> Saved</> : <><Bookmark size={14} /> Save Job</>}
            </button>
            <div className="mt-6 text-xs text-zinc-500 font-mono-alt">
              O*NET Code: <span className="text-zinc-300">{job.onet_code || "—"}</span><br />
              Posted: <span className="text-zinc-300">{job.posted_date}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
